/**
 * Extracted constants, interfaces, types, and pure helper functions
 * from canvasRenderer.ts (Phase 1 of renderer refactoring).
 *
 * This is a PURE CODE MOVE — no behavioral changes.
 */

import { clamp01, easeOutBack, easeOutCubic, easeOutQuad, lerp } from "../shared/easing";
import { CANVAS_HEIGHT, CANVAS_WIDTH, TILE_PIXEL_SIZE } from "../game/constants";
import { isMobileViewport, mobileFontSize } from "../game/platform";
import { bpmForLevel } from "../game/audioConfig";
import { prestigeStarsForLevel } from "../economy/levelSystem";
import { getSettingsPageDefinition, getSettingsPages } from "../game/settingsPages";
import type {
  CollapseAnimState,
  GamePhase,
  LeaderboardEntry,
  MatchTier,
  OpponentRenderMode,
  PowerupKind,
  SoloMode,
  SpecialKind
} from "../game/types";
import type { Rect, UiRects } from "../game/input";
import type { DailyLoginBonusStatus, DailyLoginRewardTier } from "../economy/dailyLoginRewards";
import type { VersusMatchRenderData } from "../versus/types";
import type { VersusFeedbackMode } from "../game/versusFeedbackPolicy";
import type { SpriteId } from "./assets/types";
import { GAME_TILE_COLORS } from "./themePalette";
import type { GameplayDebugSnapshot } from "../game/debugSnapshot";

export const GAME_FONT = "'Press Start 2P', monospace";
const PRESTIGE_STAR_GLYPH = "\u2605";
export const DAILY_LOGIN_MODAL_TITLE = "30-DAY LOGIN BONUS";
export const DAILY_LOGIN_MODAL_SUBTITLE = "UTC MIDNIGHT RESET";
export const DAILY_LOGIN_MODAL_LEGEND_LABELS = ["BIGGER", "MEGA"] as const;
export const DAILY_LOGIN_MODAL_LEGEND_SWATCH_SIZE = 12;
export const DAILY_LOGIN_MODAL_LEGEND_LABEL_GAP = 4;
export const DAILY_LOGIN_MODAL_LEGEND_ENTRY_GAP = 12;
export const GAMEPLAY_OBJECTIVE_SIDE_PAD = 6;
export const GAMEPLAY_OBJECTIVE_REWARD_ICON_SIZE = 14;
export const GAMEPLAY_OBJECTIVE_REWARD_ICON_TEXT_GAP = 2;
export const GAMEPLAY_OBJECTIVE_REWARD_GRANT_GAP = 6;
export const GAMEPLAY_OBJECTIVE_COLOR_SWATCH_SIZE = 7;
export const GAMEPLAY_OBJECTIVE_COLOR_SWATCH_GAP = 2;

export interface FeatureLockRenderState {
  unlocked: boolean;
  requiredLevel: number;
  signInRequired: boolean;
}

export function prestigeStarSuffix(stars: number): string {
  return stars > 0 ? ` ${PRESTIGE_STAR_GLYPH.repeat(stars)}` : "";
}

export function formatAccountLevel(level: number, stars: number, prefix = "Lv."): string {
  if (level <= 0) {
    return "";
  }
  return `${prefix}${level}${prestigeStarSuffix(stars)}`;
}

export function formatDerivedAccountLevel(level: number, prefix = "Lv."): string {
  return formatAccountLevel(level, prestigeStarsForLevel(level), prefix);
}

export type OpponentVisibilityModeLabel = "FULL" | "ADAPTIVE" | "SIMPLE";
export type OpponentAdaptiveTier = "HEALTHY" | "MODERATE" | "SEVERE";
export type OpponentVisibilityRenderTier = "FULL" | "SIMPLIFIED" | "SIMPLE";

export interface OpponentAdaptivePerfState {
  ewmaFrameMs: number;
  tier: OpponentAdaptiveTier;
  pendingTier: OpponentAdaptiveTier | null;
  pendingSec: number;
}

export interface EffectiveOpponentDisplayMode {
  mode: OpponentVisibilityModeLabel;
  renderTier: OpponentVisibilityRenderTier;
  adaptiveTier: OpponentAdaptiveTier | null;
  mobileLocked: boolean;
}

const OPPONENT_ADAPTIVE_EWMA_ALPHA = 0.18;
const OPPONENT_ADAPTIVE_DEGRADE_HOLD_SEC = 0.55;
const OPPONENT_ADAPTIVE_RECOVER_HOLD_SEC = 2.4;

function toOpponentVisibilityModeLabel(mode: OpponentRenderMode): OpponentVisibilityModeLabel {
  if (mode === "ANIMATED") {
    return "FULL";
  }
  if (mode === "SIMPLIFIED") {
    return "ADAPTIVE";
  }
  return "SIMPLE";
}

function adaptiveTierRank(tier: OpponentAdaptiveTier): number {
  if (tier === "HEALTHY") return 0;
  if (tier === "MODERATE") return 1;
  return 2;
}

function resolveAdaptiveTierCandidate(currentTier: OpponentAdaptiveTier, ewmaFrameMs: number): OpponentAdaptiveTier {
  const ms = Math.max(1, ewmaFrameMs);
  if (currentTier === "HEALTHY") {
    if (ms >= 31) return "SEVERE";
    if (ms >= 22) return "MODERATE";
    return "HEALTHY";
  }
  if (currentTier === "MODERATE") {
    if (ms >= 30) return "SEVERE";
    if (ms <= 18) return "HEALTHY";
    return "MODERATE";
  }
  if (ms <= 24) {
    return "MODERATE";
  }
  return "SEVERE";
}

export function createOpponentAdaptivePerfState(): OpponentAdaptivePerfState {
  return {
    ewmaFrameMs: 16.7,
    tier: "HEALTHY",
    pendingTier: null,
    pendingSec: 0
  };
}

export function updateOpponentAdaptivePerfState(
  state: OpponentAdaptivePerfState,
  frameMs: number,
  dtSec: number
): OpponentAdaptivePerfState {
  const nextFrameMs = Math.max(1, Math.min(100, frameMs));
  const dt = Math.max(0, Math.min(0.25, dtSec));
  const current = state ?? createOpponentAdaptivePerfState();
  const ewmaFrameMs = current.ewmaFrameMs <= 0
    ? nextFrameMs
    : lerp(current.ewmaFrameMs, nextFrameMs, OPPONENT_ADAPTIVE_EWMA_ALPHA);
  const candidate = resolveAdaptiveTierCandidate(current.tier, ewmaFrameMs);
  if (candidate === current.tier) {
    return {
      ewmaFrameMs,
      tier: current.tier,
      pendingTier: null,
      pendingSec: 0
    };
  }
  const pendingTier = candidate;
  const pendingSec = current.pendingTier === candidate ? current.pendingSec + dt : dt;
  const worsened = adaptiveTierRank(candidate) > adaptiveTierRank(current.tier);
  const holdNeeded = worsened ? OPPONENT_ADAPTIVE_DEGRADE_HOLD_SEC : OPPONENT_ADAPTIVE_RECOVER_HOLD_SEC;
  if (pendingSec >= holdNeeded) {
    return {
      ewmaFrameMs,
      tier: candidate,
      pendingTier: null,
      pendingSec: 0
    };
  }
  return {
    ewmaFrameMs,
    tier: current.tier,
    pendingTier,
    pendingSec
  };
}

export function resolveOpponentVisibilityLabel(mode: OpponentRenderMode, mobileLocked: boolean): string {
  if (mobileLocked) {
    return "SIMPLE (LOCKED)";
  }
  return toOpponentVisibilityModeLabel(mode);
}

export function resolveEffectiveOpponentDisplayMode(params: {
  requestedMode: OpponentRenderMode;
  isMobile: boolean;
  adaptivePerfState: OpponentAdaptivePerfState | null;
}): EffectiveOpponentDisplayMode {
  const { requestedMode, isMobile, adaptivePerfState } = params;
  if (isMobile) {
    return {
      mode: "SIMPLE",
      renderTier: "SIMPLE",
      adaptiveTier: null,
      mobileLocked: true
    };
  }
  if (requestedMode === "ANIMATED") {
    return {
      mode: "FULL",
      renderTier: "FULL",
      adaptiveTier: null,
      mobileLocked: false
    };
  }
  if (requestedMode === "STATIC") {
    return {
      mode: "SIMPLE",
      renderTier: "SIMPLE",
      adaptiveTier: null,
      mobileLocked: false
    };
  }
  const tier = adaptivePerfState?.tier ?? "HEALTHY";
  return {
    mode: "ADAPTIVE",
    renderTier: tier === "HEALTHY" ? "FULL" : tier === "MODERATE" ? "SIMPLIFIED" : "SIMPLE",
    adaptiveTier: tier,
    mobileLocked: false
  };
}

/** Bold font string with mobile-aware minimum size. */
export function mFont(weight: string, px: number): string {
  return `${weight} ${mobileFontSize(px)}px ${GAME_FONT}`;
}

/** Plain (no weight) font string with mobile-aware minimum size. */
export function mFontPlain(px: number): string {
  return `${mobileFontSize(px)}px ${GAME_FONT}`;
}

/** Raw pixel font string without the global mobile font floor. */
export function pxFont(px: number, weight?: string): string {
  return weight != null && weight.length > 0
    ? `${weight} ${px}px ${GAME_FONT}`
    : `${px}px ${GAME_FONT}`;
}

export function resolveSoloSelectorTextFitPx(params: {
  mobile: boolean;
  text: string;
  maxWidth: number;
  basePx: number;
  minPx: number;
  measureWidthAtPx: (fontPx: number) => number;
}): number {
  const { mobile, text, maxWidth, basePx, minPx, measureWidthAtPx } = params;
  if (!mobile || text.length === 0 || maxWidth <= 0 || basePx <= minPx) {
    return basePx;
  }

  const steps = Math.max(0, Math.round((basePx - minPx) * 2));
  for (let step = 0; step <= steps; step += 1) {
    const fontPx = basePx - step * 0.5;
    if (measureWidthAtPx(fontPx) <= maxWidth) {
      return fontPx;
    }
  }
  return minPx;
}

export interface PhoneMetrics {
  desktop: boolean;
  topPad: number;
  titleFontPx: number;
  titleGap: number;
  sectionFontPx: number;
  labelFontPx: number;
  actionFontPx: number;
  metaFontPx: number;
  helperFontPx: number;
  valueFontPx: number;
  rowH: number;
  ctaH: number;
  inlineButtonH: number;
  messageCardH: number;
  leaderboardRowH: number;
  leaderboardTabH: number;
  navButtonH: number;
  summaryH: number;
  cardRadius: number;
  rowGap: number;
  sectionGap: number;
}

export interface PhoneFrame {
  phoneX: number;
  phoneY: number;
  phoneW: number;
  phoneH: number;
  cornerR: number;
  bezelW: number;
  screenX: number;
  screenY: number;
  screenW: number;
  screenH: number;
  contentX: number;
  contentW: number;
  contentRight: number;
  dockY: number;
  dockH: number;
}

export function resolvePhoneMetrics(isMobile = isMobileViewport()): PhoneMetrics {
  if (isMobile) {
    return {
      desktop: false,
      topPad: 10,
      titleFontPx: 12,
      titleGap: 16,
      sectionFontPx: 9,
      labelFontPx: 9,
      actionFontPx: 9,
      metaFontPx: 8,
      helperFontPx: 8,
      valueFontPx: 9,
      rowH: 32,
      ctaH: 34,
      inlineButtonH: 26,
      messageCardH: 54,
      leaderboardRowH: 24,
      leaderboardTabH: 26,
      navButtonH: 26,
      summaryH: 50,
      cardRadius: 4,
      rowGap: 5,
      sectionGap: 8
    };
  }

  return {
    desktop: true,
    topPad: 8,
    titleFontPx: 11,
    titleGap: 16,
    sectionFontPx: 6,
    labelFontPx: 7,
    actionFontPx: 7,
    metaFontPx: 6,
    helperFontPx: 5,
    valueFontPx: 7,
    rowH: 28,
    ctaH: 28,
    inlineButtonH: 18,
    messageCardH: 52,
    leaderboardRowH: 18,
    leaderboardTabH: 20,
    navButtonH: 18,
    summaryH: 48,
    cardRadius: 6,
    rowGap: 4,
    sectionGap: 8
  };
}

export function resolvePhoneFrame(canvasWidth: number, canvasHeight: number): PhoneFrame {
  const phoneW = Math.min(canvasWidth - 24, 340);
  const phoneH = canvasHeight - 60;
  const phoneX = canvasWidth / 2 - phoneW / 2;
  const phoneY = 50;
  const cornerR = 12;
  const bezelW = 6;
  const screenX = phoneX + bezelW;
  const screenY = phoneY + bezelW + 20;
  const screenW = phoneW - bezelW * 2;
  const screenH = phoneH - bezelW * 2 - 20 - 72;
  const contentX = screenX + 8;
  const contentW = screenW - 16;
  return {
    phoneX,
    phoneY,
    phoneW,
    phoneH,
    cornerR,
    bezelW,
    screenX,
    screenY,
    screenW,
    screenH,
    contentX,
    contentW,
    contentRight: contentX + contentW,
    dockY: phoneY + phoneH - bezelW - 68,
    dockH: 64
  };
}

export function resolveMenuMessageBubbleWidth(textWidth: number, maxWidth = CANVAS_WIDTH - 24): number {
  return Math.min(maxWidth, Math.max(136, Math.ceil(textWidth + 32)));
}

export function resolveTitleClaimCooldownTextStyle(
  textWidth: number,
  buttonWidth: number
): { fontPx: number; scaleX: number } {
  const fontPx = 5;
  if (textWidth <= 0) {
    return { fontPx, scaleX: 1 };
  }
  return {
    fontPx,
    scaleX: Math.min(1, (buttonWidth - 10) / textWidth)
  };
}

const MOBILE_MENU_HEADER_NAME_BASE_FONT_PX = 9;
const MOBILE_MENU_HEADER_NAME_MIN_FONT_PX = 5;
const MOBILE_MENU_HEADER_LEVEL_NAME_GAP_PX = 16;
const DESKTOP_MENU_HEADER_NAME_BASE_FONT_PX = 8;
const DESKTOP_MENU_HEADER_NAME_MIN_FONT_PX = 6;
const DESKTOP_MENU_HEADER_LEVEL_NAME_GAP_PX = 10;

export interface MenuHeaderNameRowLayout {
  fontPx: number;
  scaleX: number;
  levelWidth: number;
  usernameWidth: number;
  totalWidth: number;
  usernameOffsetX: number;
}

export function resolveMobileMenuHeaderNameRowLayout(params: {
  levelPrefix: string;
  username: string;
  maxWidth: number;
  measureWidthAtPx: (fontPx: number, text: string) => number;
}): MenuHeaderNameRowLayout {
  const { levelPrefix, username, maxWidth, measureWidthAtPx } = params;
  const levelNameGap = levelPrefix.length > 0 ? MOBILE_MENU_HEADER_LEVEL_NAME_GAP_PX : 0;
  const safeMaxWidth = Math.max(0, maxWidth);
  let fontPx = MOBILE_MENU_HEADER_NAME_BASE_FONT_PX;
  let levelWidth = measureWidthAtPx(fontPx, levelPrefix);
  let usernameWidth = measureWidthAtPx(fontPx, username);
  let totalWidth = levelWidth + levelNameGap + usernameWidth;

  while (fontPx > MOBILE_MENU_HEADER_NAME_MIN_FONT_PX && totalWidth > safeMaxWidth) {
    fontPx -= 0.5;
    levelWidth = measureWidthAtPx(fontPx, levelPrefix);
    usernameWidth = measureWidthAtPx(fontPx, username);
    totalWidth = levelWidth + levelNameGap + usernameWidth;
  }

  return {
    fontPx,
    scaleX: safeMaxWidth > 0 && totalWidth > safeMaxWidth ? safeMaxWidth / totalWidth : 1,
    levelWidth,
    usernameWidth,
    totalWidth,
    usernameOffsetX: levelWidth + levelNameGap
  };
}

export function resolveDesktopMenuHeaderNameRowLayout(params: {
  levelPrefix: string;
  username: string;
  maxWidth: number;
  measureWidthAtPx: (fontPx: number, text: string) => number;
}): MenuHeaderNameRowLayout {
  const { levelPrefix, username, maxWidth, measureWidthAtPx } = params;
  const levelNameGap = levelPrefix.length > 0 ? DESKTOP_MENU_HEADER_LEVEL_NAME_GAP_PX : 0;
  const safeMaxWidth = Math.max(0, maxWidth);
  let fontPx = DESKTOP_MENU_HEADER_NAME_BASE_FONT_PX;
  let levelWidth = measureWidthAtPx(fontPx, levelPrefix);
  let usernameWidth = measureWidthAtPx(fontPx, username);
  let totalWidth = levelWidth + levelNameGap + usernameWidth;

  while (fontPx > DESKTOP_MENU_HEADER_NAME_MIN_FONT_PX && totalWidth > safeMaxWidth) {
    fontPx -= 0.5;
    levelWidth = measureWidthAtPx(fontPx, levelPrefix);
    usernameWidth = measureWidthAtPx(fontPx, username);
    totalWidth = levelWidth + levelNameGap + usernameWidth;
  }

  return {
    fontPx,
    scaleX: safeMaxWidth > 0 && totalWidth > safeMaxWidth ? safeMaxWidth / totalWidth : 1,
    levelWidth,
    usernameWidth,
    totalWidth,
    usernameOffsetX: levelWidth + levelNameGap
  };
}

const TITLE_HEADER_RESOURCE_VALUE_GAP_PX = 3;
const TITLE_HEADER_RESOURCE_STAMINA_GAP_PX = 2;
const TITLE_HEADER_RESOURCE_HOTSPOT_PAD_PX = 2;
const TITLE_HEADER_RESOURCE_GROUP_GAP_MOBILE_PX = 8;
const TITLE_HEADER_RESOURCE_GROUP_GAP_DESKTOP_PX = 12;

export interface TitleHeaderResourceGroupLayout {
  iconX: number;
  textX: number;
  left: number;
  right: number;
}

export interface TitleHeaderResourceRowLayout {
  groupGapPx: number;
  coin: TitleHeaderResourceGroupLayout;
  gem: TitleHeaderResourceGroupLayout;
  stamina: TitleHeaderResourceGroupLayout;
  coinHotspot: Rect;
  gemHotspot: Rect;
}

export function resolveTitleHeaderResourceRowLayout(params: {
  startX: number;
  centerY: number;
  mobile: boolean;
  walletIconSize: number;
  heartSize: number;
  coinTextWidth: number;
  gemTextWidth: number;
  staminaTextWidth: number;
}): TitleHeaderResourceRowLayout {
  const {
    startX,
    centerY,
    mobile,
    walletIconSize,
    heartSize,
    coinTextWidth,
    gemTextWidth,
    staminaTextWidth
  } = params;

  const safeCoinTextWidth = Math.max(0, coinTextWidth);
  const safeGemTextWidth = Math.max(0, gemTextWidth);
  const safeStaminaTextWidth = Math.max(0, staminaTextWidth);
  const groupGapPx = mobile
    ? TITLE_HEADER_RESOURCE_GROUP_GAP_MOBILE_PX
    : TITLE_HEADER_RESOURCE_GROUP_GAP_DESKTOP_PX;

  const coinIconX = startX;
  const coinTextX = coinIconX + walletIconSize + TITLE_HEADER_RESOURCE_VALUE_GAP_PX;
  const coinRight = coinTextX + safeCoinTextWidth;

  const gemIconX = coinRight + groupGapPx;
  const gemTextX = gemIconX + walletIconSize + TITLE_HEADER_RESOURCE_VALUE_GAP_PX;
  const gemRight = gemTextX + safeGemTextWidth;

  const staminaIconX = gemRight + groupGapPx;
  const staminaTextX = staminaIconX + heartSize + TITLE_HEADER_RESOURCE_STAMINA_GAP_PX;
  const staminaRight = staminaTextX + safeStaminaTextWidth;

  const hotspotY = centerY - walletIconSize / 2 - TITLE_HEADER_RESOURCE_HOTSPOT_PAD_PX;
  const hotspotH = walletIconSize + TITLE_HEADER_RESOURCE_HOTSPOT_PAD_PX * 2;

  return {
    groupGapPx,
    coin: {
      iconX: coinIconX,
      textX: coinTextX,
      left: coinIconX,
      right: coinRight
    },
    gem: {
      iconX: gemIconX,
      textX: gemTextX,
      left: gemIconX,
      right: gemRight
    },
    stamina: {
      iconX: staminaIconX,
      textX: staminaTextX,
      left: staminaIconX,
      right: staminaRight
    },
    coinHotspot: {
      x: coinIconX - TITLE_HEADER_RESOURCE_HOTSPOT_PAD_PX,
      y: hotspotY,
      w: coinRight - coinIconX + TITLE_HEADER_RESOURCE_HOTSPOT_PAD_PX * 2,
      h: hotspotH
    },
    gemHotspot: {
      x: gemIconX - TITLE_HEADER_RESOURCE_HOTSPOT_PAD_PX,
      y: hotspotY,
      w: gemRight - gemIconX + TITLE_HEADER_RESOURCE_HOTSPOT_PAD_PX * 2,
      h: hotspotH
    }
  };
}

const GEM_SHOP_BUY_KEYS = [
  "shopBuyGems0",
  "shopBuyGems1",
  "shopBuyGems2",
  "shopBuyGems3",
  "shopBuySol0",
  "shopBuySol1",
  "shopBuySol2",
  "shopBuySol3",
] as const;

const IAP_BUY_KEYS = [
  "shopBuyIap0",
  "shopBuyIap1",
  "shopBuyIap2",
  "shopBuyIap3",
] as const;

export const TILE_COLORS = GAME_TILE_COLORS;
export const COMBO_BADGE_COLOR = GAME_TILE_COLORS[1];
export const TILE_DRAW_PADDING = 1;
export const MENU_CONTENT_OFFSET_PX = 16;
export const UI_EDGE_PAD = 8;
export const UI_CLOSE_Y = UI_EDGE_PAD - MENU_CONTENT_OFFSET_PX;
export function uiCloseSize(): number { return isMobileViewport() ? 34 : 24; }
export const MENU_TEXT_PRIMARY = "#e8f7ff";
export const MENU_TEXT_SECONDARY = "#a6b7ff";
export const MENU_PANEL_FILL = "rgba(19,11,54,0.96)";
export const MENU_PANEL_STROKE = "#64e4ff";
export function menuFullButtonW(): number { return isMobileViewport() ? 280 : 196; }
function menuHalfButtonW(): number { return isMobileViewport() ? 132 : 92; }
function menuTwoColGap(): number { return isMobileViewport() ? 16 : 12; }
/** Standard button heights — mobile gets taller tap targets. */
export function menuBtnH(): number { return isMobileViewport() ? 44 : 36; }
export function menuBtnHLg(): number { return isMobileViewport() ? 52 : 44; }
/** Consistent gap between grid items and UI rows. */
export function menuGap(): number { return isMobileViewport() ? 12 : 8; }
const GAMEPLAY_STATS_PANEL_BASE_W = 244;
const GAMEPLAY_STATS_PANEL_BASE_H = 28;
function gameplayStatsPanelScale(): number {
  return isMobileViewport() ? 1.3 : 1.1;
}
const GAMEPLAY_STATS_TO_BOARD_GAP_PX = 18;
const GAMEPLAY_EXPLAINER_H = 10;
const GAMEPLAY_STACK_GAP_PX = 2;
const GAMEPLAY_PROGRESS_W = 200;
const GAMEPLAY_PROGRESS_H = 6;
const GAMEPLAY_TOP_STATS_PROGRESS_GUTTER_PX = 8;
const GAMEPLAY_TOP_STATS_PAUSE_SAFE_GAP_PX = 12;
const GAMEPLAY_TOP_STATS_ICON_TEXT_GAP_PX = 4;
const GAMEPLAY_TOP_STATS_SCORE_CASH_GAP_PX = 10;
const GAMEPLAY_TOP_STATS_FALLBACK_ICON_TEXT_GAP_PX = 3;
const GAMEPLAY_TOP_STATS_FALLBACK_SCORE_CASH_GAP_PX = 8;
export const GAMEPLAY_TOP_STATS_MOBILE_FONT_PX = 10;
export const GAMEPLAY_TOP_STATS_DESKTOP_FONT_PX = 8;
export const GAMEPLAY_TOP_STATS_FALLBACK_FONT_PX = 9;
const GAMEPLAY_TOP_STATS_MOBILE_ICON_PX = 18;
const GAMEPLAY_TOP_STATS_DESKTOP_ICON_PX = 16;
const GAMEPLAY_TOP_STATS_FALLBACK_ICON_PX = 16;
const GAMEPLAY_CUE_STACK_PADDING_PX = 8;
const GAMEPLAY_CUE_STACK_TOP_GAP_PX = 16;
const GAMEPLAY_CUE_STACK_SLOT_GAP_PX = 16;
const GAMEPLAY_CUE_STACK_BUBBLE_CLEARANCE_PX = 8;
export const GAMEPLAY_CUE_STACK_CASH_FONT_PX = 7;
export const GAMEPLAY_CUE_STACK_HYPE_FONT_PX = 10;
export const GAMEPLAY_CUE_STACK_CASH_MAX_SCALE = 1.35;
export const GAMEPLAY_CUE_STACK_HYPE_MAX_SCALE = 1.3;
const GAMEPLAY_CUE_STACK_ONBOARDING_MARGIN_PX = 6;
const GAMEPLAY_CUE_STACK_ONBOARDING_PORTRAIT_PX = 32;
const GAMEPLAY_CUE_STACK_ONBOARDING_BUBBLE_PAD_PX = 6;
const GAMEPLAY_CUE_STACK_ONBOARDING_LINE_H_PX = 8;


export function posKey(row: number, col: number): string {
  return `${row},${col}`;
}

export function compactCount(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  }
  if (abs >= 1_000) {
    return `${(value / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}K`;
  }
  return String(value);
}

export function tileBlinkFpsForLevel(level: number): number {
  return bpmForLevel(level) / 60;
}

export function musicPulseForLevel(songTimeSec: number, level: number): number {
  const bpm = Math.max(1, bpmForLevel(level));
  const beatDurationSec = 60 / bpm;
  const phase = ((songTimeSec % beatDurationSec) + beatDurationSec) % beatDurationSec;
  return 0.5 + 0.5 * Math.sin((phase / beatDurationSec) * Math.PI * 2);
}

export function grooveMeterScaleForFill(fill: number): number {
  return 1 + clamp01(fill) * 0.1;
}

const GROOVE_READY_OUTLINE_COLORS = ["#00d4ff", "#8a5cff", "#ff66cc"] as const;
const GROOVE_READY_COLOR_STEP_MS = 250;
const GROOVE_READY_COLOR_STEP_REDUCED_MS = 600;
const GROOVE_READY_PULSE_PERIOD_MS = 1000;

export interface GrooveReadyCueStyle {
  outlineColor: typeof GROOVE_READY_OUTLINE_COLORS[number];
  shadowColor: string;
  lineWidth: number;
  shadowBlur: number;
  scale: number;
}

export function resolveGrooveReadyCueStyle(
  nowMs: number,
  reducedMotionPreferred: boolean
): GrooveReadyCueStyle {
  const safeNowMs = Math.max(0, nowMs);
  const stepMs = reducedMotionPreferred
    ? GROOVE_READY_COLOR_STEP_REDUCED_MS
    : GROOVE_READY_COLOR_STEP_MS;
  const colorIndex = Math.floor(safeNowMs / stepMs) % GROOVE_READY_OUTLINE_COLORS.length;
  const outlineColor = GROOVE_READY_OUTLINE_COLORS[colorIndex] ?? GROOVE_READY_OUTLINE_COLORS[0];
  if (reducedMotionPreferred) {
    return {
      outlineColor,
      shadowColor: outlineColor,
      lineWidth: 2.2,
      shadowBlur: 8,
      scale: 1.04
    };
  }
  const pulsePhase = ((safeNowMs % GROOVE_READY_PULSE_PERIOD_MS) / GROOVE_READY_PULSE_PERIOD_MS) * Math.PI * 2
    - Math.PI / 2;
  const pulse = 0.5 + 0.5 * Math.sin(pulsePhase);
  return {
    outlineColor,
    shadowColor: outlineColor,
    lineWidth: 2 + pulse * 0.8,
    shadowBlur: 8 + pulse * 8,
    scale: 1 + pulse * 0.05
  };
}

export function grooveStatusWord(armed: boolean, active: boolean): string {
  if (active) return "ON";
  if (armed) return "READY";
  return "BUILD";
}

export function microLabelWidthForControl(controlWidth: number): number {
  return controlWidth * 1.12;
}

export function resolveDisplayedSpinsCount(
  rotationKeys: number,
  groove: { active?: boolean; freeRotationsRemaining?: number } | null | undefined
): number {
  const safeRotationKeys = Number.isFinite(rotationKeys)
    ? Math.max(0, Math.floor(rotationKeys))
    : 0;
  const freeRotationsRaw = groove?.freeRotationsRemaining ?? 0;
  const safeFreeRotations = Number.isFinite(freeRotationsRaw)
    ? Math.max(0, Math.floor(freeRotationsRaw))
    : 0;
  if (groove?.active === true && safeFreeRotations > 0) {
    return safeFreeRotations;
  }
  return safeRotationKeys;
}

export function grooveFillLiftForFrac(grooveFillFrac: number): number {
  return 0.25 + 0.75 * clamp01(grooveFillFrac);
}

export function grooveMeterTitle(grooveReady: boolean): string {
  return grooveReady ? "DROP IT!" : "HYPE";
}

export function grooveMeterBottomText(active: boolean, activeRemainingSec: number): string {
  if (!active) {
    return "";
  }
  return `${Math.max(0, activeRemainingSec).toFixed(1)}s`;
}

export const DOOM_DANGER_ENTER_THRESHOLD = 0.85;
export const DOOM_DANGER_EXIT_THRESHOLD = 0.8;
export const GAMEPLAY_BADGE_BASE_SCALE = 0.8;
export const GAMEPLAY_BADGE_BASE_OPACITY = 0.72;
const DOOM_FILL_EMPTY_RGB = { r: 255, g: 68, b: 68 } as const; // #ff4444
const DOOM_FILL_FULL_RGB = { r: 0, g: 255, b: 136 } as const; // #00ff88
// Spins pill removed (counts shown on CDJ turntables)

export function isSinglePlayerHudPolishPhase(phase: GamePhase): boolean {
  return phase === "RUN_PLAYABLE" || phase === "RUN_RESOLVING" || phase === "RUN_LEVELUP" || phase === "VERSUS_MATCH";
}

export function resolveDoomDangerFraction(doomClockSec: number, doomClockMaxSec: number): number {
  const safeMax = Math.max(1, doomClockMaxSec);
  const timeLeftFrac = clamp01(doomClockSec / safeMax);
  return clamp01(1 - timeLeftFrac);
}

export function resolveDoomDangerActive(
  wasActive: boolean,
  dangerFrac: number,
  enterThreshold = DOOM_DANGER_ENTER_THRESHOLD,
  exitThreshold = DOOM_DANGER_EXIT_THRESHOLD
): boolean {
  const frac = clamp01(dangerFrac);
  if (wasActive) {
    return frac >= exitThreshold;
  }
  return frac >= enterThreshold;
}

export function resolveDoomFillRgb(doomFillFrac: number): { r: number; g: number; b: number } {
  const frac = clamp01(doomFillFrac);
  return {
    r: Math.round(lerp(DOOM_FILL_EMPTY_RGB.r, DOOM_FILL_FULL_RGB.r, frac)),
    g: Math.round(lerp(DOOM_FILL_EMPTY_RGB.g, DOOM_FILL_FULL_RGB.g, frac)),
    b: Math.round(lerp(DOOM_FILL_EMPTY_RGB.b, DOOM_FILL_FULL_RGB.b, frac))
  };
}

export function resolveDoomFillCss(doomFillFrac: number): string {
  const rgb = resolveDoomFillRgb(doomFillFrac);
  return `rgb(${rgb.r},${rgb.g},${rgb.b})`;
}

export function resolveLegacyDoomRampCss(blockFrac: number): string {
  const frac = clamp01(blockFrac);
  let r: number;
  let g: number;
  let b: number;
  if (frac < 0.25) {
    const t = frac / 0.25;
    r = Math.round(lerp(100, 120, t));
    g = Math.round(lerp(220, 210, t));
    b = Math.round(lerp(200, 160, t));
  } else if (frac < 0.5) {
    const t = (frac - 0.25) / 0.25;
    r = Math.round(lerp(120, 190, t));
    g = Math.round(lerp(210, 220, t));
    b = Math.round(lerp(160, 100, t));
  } else if (frac < 0.75) {
    const t = (frac - 0.5) / 0.25;
    r = Math.round(lerp(190, 220, t));
    g = Math.round(lerp(220, 160, t));
    b = Math.round(lerp(100, 60, t));
  } else {
    const t = (frac - 0.75) / 0.25;
    r = Math.round(lerp(220, 160, t));
    g = Math.round(lerp(160, 100, t));
    b = Math.round(lerp(60, 60, t));
  }
  return `rgb(${r},${g},${b})`;
}

export function resolveMiniDoomSegmentCss(fillOrderFrac: number): string {
  // First filled segment is the danger end (red); last filled is safe (green).
  return resolveLegacyDoomRampCss(1 - clamp01(fillOrderFrac));
}

export function grooveFilledSegmentCount(fill: number, segments: number): number {
  const safeSegments = Math.max(1, Math.floor(segments));
  return Math.round(clamp01(fill) * safeSegments);
}

export function grooveShimmerStrengthForFill(fill: number, reducedMotionPreferred: boolean): number {
  const frac = clamp01(fill);
  const base = 0.1 + frac * 0.28;
  return reducedMotionPreferred ? base * 0.45 : base;
}

export function grooveShimmerSpeedForFill(fill: number, reducedMotionPreferred: boolean): number {
  const frac = clamp01(fill);
  const base = 22 + frac * 52;
  return reducedMotionPreferred ? base * 0.55 : base;
}

export interface GameplayBadgeStyle {
  scale: number;
  opacity: number;
  popActive: boolean;
}

export function resolveGameplayBadgeStyle(
  singlePlayerHudPolish: boolean,
  grooveReady: boolean,
  doomDangerActive: boolean,
  chainDepth: number
): GameplayBadgeStyle {
  if (!singlePlayerHudPolish) {
    return { scale: 1, opacity: 1, popActive: false };
  }
  const popActive = grooveReady || doomDangerActive || chainDepth >= 4;
  return {
    scale: GAMEPLAY_BADGE_BASE_SCALE,
    opacity: GAMEPLAY_BADGE_BASE_OPACITY,
    popActive
  };
}

export function gameplayBadgeMomentIntensity(
  popActive: boolean,
  reducedMotionPreferred: boolean,
  pulse01: number
): number {
  if (!popActive) {
    return 0;
  }
  const pulse = clamp01(pulse01);
  return reducedMotionPreferred ? 0.55 : 0.55 + pulse * 0.45;
}

export interface GameplayHeaderAnchors {
  statsPanelX: number;
  statsPanelY: number;
  statsPanelW: number;
  statsPanelH: number;
  explainerY: number;
  explainerH: number;
  progressX: number;
  progressY: number;
  progressW: number;
  progressH: number;
}

export type GameplayGoalChipVariant = "text" | "graphic";

export interface GameplayGoalChipLayout {
  chipH: number;
  chipMinW: number;
  chipLeftPad: number;
  chipRightPad: number;
  chipIconSize: number;
  chipIconGap: number;
  chipProgressGap: number;
  chipLabelFontPx: number;
  chipProgressFontPx: number;
  chipDetailFontPx: number;
  chipLabelY: number;
  chipDetailY: number;
  chipIconY: number;
  rowRushBadgeFontPx: number;
  rowRushBadgePadX: number;
  rowRushBadgeGap: number;
  rowRushBadgeH: number;
}

export interface ColorHuntGoalPreviewLayout {
  previewSize: number;
  previewGap: number;
}

export interface GameplayTopStatShadowStyle {
  color: string;
  offsetX: number;
  offsetY: number;
  blur: number;
}

interface GameplayTopStatMetrics {
  fontPx: number;
  iconSize: number;
  iconTextGap: number;
  scoreCashGap: number;
}

export interface GameplayTopStatLayout {
  fontPx: number;
  iconSize: number;
  iconTextGap: number;
  scoreCashGap: number;
  safeRightX: number;
  minLeftX: number;
  leftX: number;
  rightX: number;
  totalWidth: number;
  scoreIconX: number;
  scoreTextX: number;
  cashIconX: number;
  cashTextX: number;
  fallbackUsed: boolean;
  fitsAvailableWidth: boolean;
}

function resolvePreferredGameplayTopStatMetrics(mobile: boolean): GameplayTopStatMetrics {
  return mobile
    ? {
        fontPx: GAMEPLAY_TOP_STATS_MOBILE_FONT_PX,
        iconSize: GAMEPLAY_TOP_STATS_MOBILE_ICON_PX,
        iconTextGap: GAMEPLAY_TOP_STATS_ICON_TEXT_GAP_PX,
        scoreCashGap: GAMEPLAY_TOP_STATS_SCORE_CASH_GAP_PX
      }
    : {
        fontPx: GAMEPLAY_TOP_STATS_DESKTOP_FONT_PX,
        iconSize: GAMEPLAY_TOP_STATS_DESKTOP_ICON_PX,
        iconTextGap: GAMEPLAY_TOP_STATS_ICON_TEXT_GAP_PX,
        scoreCashGap: GAMEPLAY_TOP_STATS_SCORE_CASH_GAP_PX
      };
}

function resolveFallbackGameplayTopStatMetrics(): GameplayTopStatMetrics {
  return {
    fontPx: GAMEPLAY_TOP_STATS_FALLBACK_FONT_PX,
    iconSize: GAMEPLAY_TOP_STATS_FALLBACK_ICON_PX,
    iconTextGap: GAMEPLAY_TOP_STATS_FALLBACK_ICON_TEXT_GAP_PX,
    scoreCashGap: GAMEPLAY_TOP_STATS_FALLBACK_SCORE_CASH_GAP_PX
  };
}

function buildGameplayTopStatLayout(params: {
  progressBarRightX: number;
  pauseButtonLeftX: number;
  scoreWidth: number;
  cashWidth: number;
  metrics: GameplayTopStatMetrics;
  fallbackUsed: boolean;
}): GameplayTopStatLayout {
  const {
    progressBarRightX,
    pauseButtonLeftX,
    scoreWidth,
    cashWidth,
    metrics,
    fallbackUsed
  } = params;
  const safeRightX = pauseButtonLeftX - GAMEPLAY_TOP_STATS_PAUSE_SAFE_GAP_PX;
  const minLeftX = progressBarRightX + GAMEPLAY_TOP_STATS_PROGRESS_GUTTER_PX;
  const safeScoreWidth = Math.max(0, scoreWidth);
  const safeCashWidth = Math.max(0, cashWidth);
  const cashTextX = safeRightX - safeCashWidth;
  const cashIconX = cashTextX - metrics.iconTextGap - metrics.iconSize;
  const scoreTextX = cashIconX - metrics.scoreCashGap - safeScoreWidth;
  const scoreIconX = scoreTextX - metrics.iconTextGap - metrics.iconSize;
  const leftX = scoreIconX;

  return {
    fontPx: metrics.fontPx,
    iconSize: metrics.iconSize,
    iconTextGap: metrics.iconTextGap,
    scoreCashGap: metrics.scoreCashGap,
    safeRightX,
    minLeftX,
    leftX,
    rightX: safeRightX,
    totalWidth: safeRightX - leftX,
    scoreIconX,
    scoreTextX,
    cashIconX,
    cashTextX,
    fallbackUsed,
    fitsAvailableWidth: leftX >= minLeftX
  };
}

export function resolveGameplayTopStatLayout(params: {
  progressBarRightX: number;
  pauseButtonLeftX: number;
  scoreWidth: number;
  cashWidth: number;
  mobile: boolean;
  fallbackScoreWidth?: number;
  fallbackCashWidth?: number;
}): GameplayTopStatLayout {
  const preferredLayout = buildGameplayTopStatLayout({
    progressBarRightX: params.progressBarRightX,
    pauseButtonLeftX: params.pauseButtonLeftX,
    scoreWidth: params.scoreWidth,
    cashWidth: params.cashWidth,
    metrics: resolvePreferredGameplayTopStatMetrics(params.mobile),
    fallbackUsed: false
  });
  if (!params.mobile || preferredLayout.fitsAvailableWidth) {
    return preferredLayout;
  }

  return buildGameplayTopStatLayout({
    progressBarRightX: params.progressBarRightX,
    pauseButtonLeftX: params.pauseButtonLeftX,
    scoreWidth: params.fallbackScoreWidth ?? params.scoreWidth,
    cashWidth: params.fallbackCashWidth ?? params.cashWidth,
    metrics: resolveFallbackGameplayTopStatMetrics(),
    fallbackUsed: true
  });
}

type GameplayTopRightHudControl = "pause" | "settings";

export function resolveGameplayTopRightHudControl(phase: GamePhase): GameplayTopRightHudControl {
  return phase === "VERSUS_MATCH" ? "settings" : "pause";
}

export function resolveGameplayTopRightHudRect(buttonSize: number): Rect {
  return {
    x: CANVAS_WIDTH - buttonSize - UI_EDGE_PAD,
    y: UI_EDGE_PAD,
    w: buttonSize,
    h: buttonSize
  };
}

export function resolveVersusOptionsPanelRect(settingsRect: Rect, mobile: boolean, canvasWidth: number): Rect {
  const panelPad = mobile ? 5 : 6;
  const rowH = mobile ? 18 : 20;
  const rowGap = 4;
  const panelW = mobile ? 156 : 172;
  const panelH = panelPad * 2 + rowH * 3 + rowGap * 2;
  const preferredX = settingsRect.x + settingsRect.w - panelW;
  const maxX = Math.max(0, canvasWidth - panelW);
  return {
    x: Math.max(0, Math.min(maxX, preferredX)),
    y: settingsRect.y + settingsRect.h + 6,
    w: panelW,
    h: panelH
  };
}

export interface GameplayCueStackLayout {
  laneLeftX: number;
  laneRightX: number;
  laneWidth: number;
  firstSlotY: number;
  secondSlotY: number;
  cashPopupX: number;
  cashPopupY: number;
  hypeTextX: number;
  objectiveReservedWidth: number;
}

export interface OnboardingRafsbyLayout {
  portraitX: number;
  portraitY: number;
  portraitSize: number;
  bubbleX: number;
  bubbleY: number;
  bubbleW: number;
  bubbleH: number;
  bubbleBottomY: number;
  bubblePad: number;
  lineH: number;
  lines: string[];
}

export interface MobileVersusStatsRowLayout {
  leftX: number;
  rightX: number;
  statsY: number;
  barY: number;
  scoreY: number;
  doomBarW: number;
  doomBarH: number;
  playerBarX: number;
  opponentBarX: number;
  playerBarRight: number;
  opponentBarLeft: number;
  barGap: number;
}

export function resolveGameplayCueStackLayout(params: {
  topStatLayout: GameplayTopStatLayout;
  topStatBottomY: number;
  cashGroupCenterX: number;
  widestCueWidth: number;
  onboardingBubbleBottomY?: number | null;
}): GameplayCueStackLayout | null {
  const laneWidth = Math.ceil(Math.max(0, params.widestCueWidth) + GAMEPLAY_CUE_STACK_PADDING_PX);
  if (laneWidth <= GAMEPLAY_CUE_STACK_PADDING_PX) {
    return null;
  }
  const laneRightX = params.topStatLayout.safeRightX;
  const laneLeftX = laneRightX - laneWidth;
  const firstSlotY = Math.max(
    params.topStatBottomY + GAMEPLAY_CUE_STACK_TOP_GAP_PX,
    (params.onboardingBubbleBottomY ?? Number.NEGATIVE_INFINITY) + GAMEPLAY_CUE_STACK_BUBBLE_CLEARANCE_PX
  );
  return {
    laneLeftX,
    laneRightX,
    laneWidth,
    firstSlotY,
    secondSlotY: firstSlotY + GAMEPLAY_CUE_STACK_SLOT_GAP_PX,
    cashPopupX: Math.max(laneLeftX + 1, Math.min(laneRightX - 1, params.cashGroupCenterX)),
    cashPopupY: firstSlotY,
    hypeTextX: laneRightX,
    objectiveReservedWidth: laneWidth
  };
}

export function resolveOnboardingRafsbyLayout(params: {
  canvasWidth: number;
  message: string;
  measureTextWidth: (text: string) => number;
  portraitSize?: number;
  margin?: number;
  bubblePad?: number;
  bubbleMaxWidth?: number;
  lineH?: number;
}): OnboardingRafsbyLayout {
  const portraitSize = params.portraitSize ?? GAMEPLAY_CUE_STACK_ONBOARDING_PORTRAIT_PX;
  const margin = params.margin ?? GAMEPLAY_CUE_STACK_ONBOARDING_MARGIN_PX;
  const bubblePad = params.bubblePad ?? GAMEPLAY_CUE_STACK_ONBOARDING_BUBBLE_PAD_PX;
  const bubbleMaxWidth = params.bubbleMaxWidth ?? params.canvasWidth * 0.55;
  const lineH = params.lineH ?? GAMEPLAY_CUE_STACK_ONBOARDING_LINE_H_PX;
  const portraitX = params.canvasWidth - portraitSize - margin;
  const portraitY = margin + 2;
  const maxLineWidth = Math.max(0, bubbleMaxWidth - bubblePad * 2);
  const words = params.message.split(" ");
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const testLine = line ? `${line} ${word}` : word;
    if (params.measureTextWidth(testLine) > maxLineWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = testLine;
    }
  }
  if (line) {
    lines.push(line);
  }
  if (lines.length === 0) {
    lines.push("");
  }
  const widestLine = lines.reduce((maxWidth, currentLine) => (
    Math.max(maxWidth, params.measureTextWidth(currentLine))
  ), 0);
  const bubbleH = lines.length * lineH + bubblePad * 2;
  const bubbleW = Math.min(bubbleMaxWidth, widestLine + bubblePad * 2 + 10);
  const bubbleX = portraitX - bubbleW - 4;
  const bubbleY = portraitY + (portraitSize - bubbleH) / 2;
  return {
    portraitX,
    portraitY,
    portraitSize,
    bubbleX,
    bubbleY,
    bubbleW,
    bubbleH,
    bubbleBottomY: bubbleY + bubbleH,
    bubblePad,
    lineH,
    lines
  };
}

export function resolveGameplayHeaderAnchors(
  boardTopY: number,
  levelBadgeY: number,
  levelBadgeH: number
): GameplayHeaderAnchors {
  const statsPanelW = Math.round(GAMEPLAY_STATS_PANEL_BASE_W * gameplayStatsPanelScale());
  const statsPanelH = Math.round(GAMEPLAY_STATS_PANEL_BASE_H * gameplayStatsPanelScale());
  const statsPanelX = Math.floor((CANVAS_WIDTH - statsPanelW) / 2);
  const explainerH = GAMEPLAY_EXPLAINER_H;
  const statsPanelY = boardTopY - statsPanelH - GAMEPLAY_STATS_TO_BOARD_GAP_PX;
  const explainerY = statsPanelY - explainerH - GAMEPLAY_STACK_GAP_PX;
  const progressW = GAMEPLAY_PROGRESS_W;
  const progressH = GAMEPLAY_PROGRESS_H;
  const progressX = Math.floor((CANVAS_WIDTH - progressW) / 2);
  const progressY = levelBadgeY + Math.floor((levelBadgeH - progressH) / 2);
  return {
    statsPanelX,
    statsPanelY,
    statsPanelW,
    statsPanelH,
    explainerY,
    explainerH,
    progressX,
    progressY,
    progressW,
    progressH
  };
}

export function resolveMobileVersusStatsRowLayout(
  baseY: number,
  canvasWidth: number
): MobileVersusStatsRowLayout {
  const leftX = 70;
  const rightX = canvasWidth - 70;
  const midX = Math.floor(canvasWidth / 2);
  const barGap = 6; // gap between the two doom bars in the center
  const doomBarH = 20;
  const statsY = baseY + 100;
  const scoreY = statsY + 13;
  const barY = scoreY + 16;
  // Bars fill from edges toward center (matching desktop style)
  const playerBarX = leftX;
  const doomBarW = midX - barGap / 2 - leftX;
  const opponentBarX = midX + barGap / 2;
  const playerBarRight = playerBarX + doomBarW;
  const opponentBarLeft = opponentBarX;
  return {
    leftX,
    rightX,
    statsY,
    barY,
    scoreY,
    doomBarW,
    doomBarH,
    playerBarX,
    opponentBarX,
    playerBarRight,
    opponentBarLeft,
    barGap
  };
}

export function resolveGameplayGoalChipLayout(
  mobile: boolean,
  variant: GameplayGoalChipVariant
): GameplayGoalChipLayout {
  if (mobile) {
    return variant === "graphic"
      ? {
          chipH: 34,
          chipMinW: 220,
          chipLeftPad: 8,
          chipRightPad: 8,
          chipIconSize: 10,
          chipIconGap: 6,
          chipProgressGap: 10,
          chipLabelFontPx: 10,
          chipProgressFontPx: 11,
          chipDetailFontPx: 9,
          chipLabelY: 11,
          chipDetailY: 25,
          chipIconY: 6,
          rowRushBadgeFontPx: 9,
          rowRushBadgePadX: 5,
          rowRushBadgeGap: 5,
          rowRushBadgeH: 12
        }
      : {
          chipH: 30,
          chipMinW: 220,
          chipLeftPad: 8,
          chipRightPad: 8,
          chipIconSize: 10,
          chipIconGap: 6,
          chipProgressGap: 10,
          chipLabelFontPx: 10,
          chipProgressFontPx: 11,
          chipDetailFontPx: 9,
          chipLabelY: 11,
          chipDetailY: 22,
          chipIconY: 6,
          rowRushBadgeFontPx: 9,
          rowRushBadgePadX: 5,
          rowRushBadgeGap: 5,
          rowRushBadgeH: 12
        };
  }

  return variant === "graphic"
    ? {
        chipH: 30,
        chipMinW: 236,
        chipLeftPad: 8,
        chipRightPad: 8,
        chipIconSize: 9,
        chipIconGap: 6,
        chipProgressGap: 10,
        chipLabelFontPx: 6,
        chipProgressFontPx: 7,
        chipDetailFontPx: 5,
        chipLabelY: 10,
        chipDetailY: 22,
        chipIconY: 5,
        rowRushBadgeFontPx: 5,
        rowRushBadgePadX: 5,
        rowRushBadgeGap: 5,
        rowRushBadgeH: 11
      }
    : {
        chipH: 26,
        chipMinW: 236,
        chipLeftPad: 8,
        chipRightPad: 8,
        chipIconSize: 9,
        chipIconGap: 6,
        chipProgressGap: 10,
        chipLabelFontPx: 6,
        chipProgressFontPx: 7,
        chipDetailFontPx: 5,
        chipLabelY: 9,
        chipDetailY: 19,
        chipIconY: 4,
        rowRushBadgeFontPx: 5,
        rowRushBadgePadX: 5,
        rowRushBadgeGap: 5,
        rowRushBadgeH: 11
      };
}

export function resolveColorHuntGoalPreviewLayout(mobile: boolean): ColorHuntGoalPreviewLayout {
  return mobile
    ? {
        previewSize: 14,
        previewGap: 6
      }
    : {
        previewSize: 16,
        previewGap: 6
      };
}

export function resolveGameplayObjectiveRowHeight(mobile: boolean): number {
  return mobile ? 26 : 32;
}

export function resolveGameplayObjectiveNumberBoxWidth(mobile: boolean): number {
  return mobile ? 26 : 32;
}

export function resolveGameplayObjectiveSubheadingFontPx(mobile: boolean): number {
  return mobile ? 5 : 6;
}

export function resolveGameplayObjectiveSubtextFontPx(mobile: boolean): number {
  return mobile ? 8 : 6;
}

export type GameplayObjectiveLowerLineWidthParams =
  | { kind: "text"; textWidth: number; }
  | {
      kind: "color-hunt";
      prefixWidth: number;
      suffixWidth: number;
      swatchCount: number;
      swatchSize: number;
      swatchGap: number;
    }
  | {
      kind: "reward";
      amountWidths: number[];
      iconSize: number;
      iconTextGap: number;
      grantGap: number;
    };

export function resolveGameplayObjectiveLowerLineWidth(params: GameplayObjectiveLowerLineWidthParams): number {
  if (params.kind === "text") {
    return Math.max(0, params.textWidth);
  }
  if (params.kind === "color-hunt") {
    return Math.max(0, params.prefixWidth)
      + Math.max(0, params.suffixWidth)
      + Math.max(0, params.swatchCount) * (Math.max(0, params.swatchSize) + Math.max(0, params.swatchGap));
  }
  return params.amountWidths.reduce((total, width) => (
    total
    + Math.max(0, params.iconSize)
    + Math.max(0, params.iconTextGap)
    + Math.max(0, width)
    + Math.max(0, params.grantGap)
  ), 0);
}

export interface GameplayObjectiveBlockLayout {
  labelBarX: number;
  labelBarW: number;
  numBoxX: number;
  numBoxW: number;
  rightX: number;
  totalWidth: number;
  contentMaxWidth: number;
}

export function resolveGameplayObjectiveBlockLayout(params: {
  leftX: number;
  maxRightX: number;
  numBoxW: number;
  gap: number;
  paddingLeft: number;
  paddingRight: number;
  topLineWidth: number;
  bottomLineWidth: number;
}): GameplayObjectiveBlockLayout {
  const {
    leftX,
    maxRightX,
    numBoxW,
    gap,
    paddingLeft,
    paddingRight,
    topLineWidth,
    bottomLineWidth
  } = params;
  const safeGap = Math.max(0, gap);
  const safeNumBoxW = Math.max(0, numBoxW);
  const contentWidth = Math.ceil(Math.max(0, topLineWidth, bottomLineWidth));
  const labelBarW = contentWidth + Math.max(0, paddingLeft) + Math.max(0, paddingRight);
  const minRightX = leftX + safeGap + safeNumBoxW;
  const unclampedRightX = leftX + labelBarW + safeGap + safeNumBoxW;
  const rightX = Math.max(minRightX, Math.min(maxRightX, unclampedRightX));
  const fittedLabelBarW = Math.max(0, rightX - leftX - safeGap - safeNumBoxW);
  return {
    labelBarX: leftX,
    labelBarW: fittedLabelBarW,
    numBoxX: leftX + fittedLabelBarW + safeGap,
    numBoxW: safeNumBoxW,
    rightX,
    totalWidth: rightX - leftX,
    contentMaxWidth: Math.max(0, fittedLabelBarW - Math.max(0, paddingLeft) - Math.max(0, paddingRight))
  };
}

export function resolveGameplayTopStatShadowStyle(): GameplayTopStatShadowStyle {
  return {
    color: "rgba(0,0,0,0.55)",
    offsetX: 1,
    offsetY: 1,
    blur: 0
  };
}

export interface WipeDissolveFxConfig {
  rainbowBurstCount: number;
  showWipeWaveOverlay: boolean;
}

export interface CrateBreakFxConfig {
  visible: boolean;
  alpha: number;
  travelPx: number;
}

export function resolveWipeDissolveFxConfig(feedbackMode: VersusFeedbackMode): WipeDissolveFxConfig {
  if (feedbackMode === "full") {
    return { rainbowBurstCount: 10, showWipeWaveOverlay: true };
  }
  if (feedbackMode === "reduced") {
    return { rainbowBurstCount: 5, showWipeWaveOverlay: false };
  }
  return { rainbowBurstCount: 0, showWipeWaveOverlay: false };
}

export function resolveCrateBreakFxConfig(rawProgress: number, feedbackMode: VersusFeedbackMode): CrateBreakFxConfig {
  if (feedbackMode === "off") {
    return { visible: false, alpha: 0, travelPx: 0 };
  }

  const progress = clamp01(rawProgress);
  const eased = easeOutCubic(progress);
  const travelScale = feedbackMode === "reduced" ? 0.12 : 0.22;
  const alpha = Math.max(0, 1 - eased);

  return {
    visible: alpha > 0.01,
    alpha,
    travelPx: TILE_PIXEL_SIZE * travelScale * eased
  };
}

export const LEVEL_BADGE_WIGGLE_DURATION_MS = 450;
const LEVEL_BADGE_WIGGLE_MAX_ROTATION_RAD = 0.08;
const LEVEL_BADGE_WIGGLE_MAX_SCALE_DELTA = 0.06;
export const LEVEL_BADGE_REDUCED_MOTION_SCALE = 0.12;

export interface LevelBadgeTheme {
  fill: string;
  stroke: string;
  text: string;
}

export interface LevelBadgeWiggle {
  rotationRad: number;
  scale: number;
  done: boolean;
}

export function resolveLevelBadgeTheme(level: number): LevelBadgeTheme {
  const safeLevel = Math.max(1, Math.floor(level));
  const hueBase = ((safeLevel * 37) % 360 + 360) % 360;
  return {
    fill: `hsla(${hueBase.toFixed(1)},52%,16%,0.92)`,
    stroke: `hsla(${((hueBase + 22) % 360).toFixed(1)},88%,72%,0.95)`,
    text: `hsla(${((hueBase + 8) % 360).toFixed(1)},96%,88%,1)`
  };
}

export function resolveLevelBadgeWiggle(elapsedMs: number): LevelBadgeWiggle {
  const safeElapsedMs = Math.max(0, elapsedMs);
  if (safeElapsedMs >= LEVEL_BADGE_WIGGLE_DURATION_MS) {
    return { rotationRad: 0, scale: 1, done: true };
  }
  const t = safeElapsedMs / LEVEL_BADGE_WIGGLE_DURATION_MS;
  const envelope = Math.pow(1 - t, 2);
  const rotationRad = Math.sin(t * Math.PI * 6) * LEVEL_BADGE_WIGGLE_MAX_ROTATION_RAD * envelope;
  const scale = 1 + Math.sin(t * Math.PI * 3) * LEVEL_BADGE_WIGGLE_MAX_SCALE_DELTA * envelope;
  return {
    rotationRad,
    scale,
    done: false
  };
}

export interface GrooveActiveOutlineStyle {
  hueA: number;
  hueB: number;
  hueC: number;
  alpha: number;
  lineWidth: number;
  shadowAlpha: number;
  shadowBlur: number;
}

export function resolveGrooveActiveOutline(
  songTimeSec: number,
  bpm: number,
  grooveFillFrac: number
): GrooveActiveOutlineStyle {
  const safeBpm = Math.max(1, bpm);
  const beatDurationSec = 60 / safeBpm;
  const beatPhase = ((songTimeSec % beatDurationSec) + beatDurationSec) % beatDurationSec;
  const beatPulse = 0.5 + 0.5 * Math.sin((beatPhase / beatDurationSec) * Math.PI * 2);
  const fill = clamp01(grooveFillFrac);
  const hueA = ((songTimeSec * safeBpm * 1.35) % 360 + 360) % 360;
  const hueB = (hueA + 120) % 360;
  const hueC = (hueA + 240) % 360;
  return {
    hueA,
    hueB,
    hueC,
    alpha: clamp01(0.44 + beatPulse * 0.28 + fill * 0.14),
    lineWidth: 1.9 + fill * 0.9 + beatPulse * 0.9,
    shadowAlpha: clamp01(0.38 + beatPulse * 0.3 + fill * 0.12),
    shadowBlur: 6 + beatPulse * 6 + fill * 4
  };
}

export interface RowRushTargetRowVisual {
  hueA: number;
  hueB: number;
  hueC: number;
  pulse: number;
  fillAlpha: number;
  outlineAlpha: number;
  innerAlpha: number;
  shadowAlpha: number;
  shadowBlur: number;
}

export function rowRushTargetRowBadgeLabel(row: number): string {
  return `ROW ${row + 1}`;
}

export function resolveRowRushTargetRowVisual(
  nowMs: number,
  row: number,
  reducedMotionPreferred: boolean,
  completed = false
): RowRushTargetRowVisual {
  if (completed) {
    const pulse = reducedMotionPreferred ? 0.92 : 0.76 + 0.24 * Math.sin(nowMs / 260 + row * 0.28);
    return {
      hueA: 138,
      hueB: 182,
      hueC: 54,
      pulse,
      fillAlpha: clamp01(0.34 + pulse * 0.08),
      outlineAlpha: clamp01(0.9 + pulse * 0.08),
      innerAlpha: clamp01(0.54 + pulse * 0.1),
      shadowAlpha: clamp01(0.58 + pulse * 0.18),
      shadowBlur: reducedMotionPreferred ? 5 : 12 + pulse * 6
    };
  }
  const baseHue = reducedMotionPreferred
    ? (24 + row * 47) % 360
    : ((nowMs * 0.11) + row * 43) % 360;
  const pulse = reducedMotionPreferred ? 0.82 : 0.58 + 0.42 * Math.sin(nowMs / 210 + row * 0.8);
  return {
    hueA: (baseHue + 360) % 360,
    hueB: (baseHue + 120) % 360,
    hueC: (baseHue + 240) % 360,
    pulse,
    fillAlpha: clamp01(0.28 + pulse * 0.14),
    outlineAlpha: clamp01(0.82 + pulse * 0.14),
    innerAlpha: clamp01(0.44 + pulse * 0.16),
    shadowAlpha: clamp01(0.5 + pulse * 0.18),
    shadowBlur: reducedMotionPreferred ? 4 : 10 + pulse * 5
  };
}

export function rowRushVisualParticleColors(visual: RowRushTargetRowVisual): string[] {
  return [
    `hsl(${visual.hueA.toFixed(1)}, 100%, 68%)`,
    `hsl(${visual.hueB.toFixed(1)}, 100%, 74%)`,
    `hsl(${visual.hueC.toFixed(1)}, 100%, 70%)`,
    "#f6fdff"
  ];
}

export function shouldDrawSidePanelTelemetry(phase: GamePhase): boolean {
  return !(
    phase === "RUN_PLAYABLE"
    || phase === "RUN_RESOLVING"
    || phase === "RUN_LEVELUP"
  );
}

export function shouldRenderDebugOverlay(
  debugViewModel: GameplayDebugSnapshot | null
): debugViewModel is GameplayDebugSnapshot {
  return debugViewModel != null;
}

export interface DebugOverlaySections {
  compact: boolean;
  gameplay: string[];
  bot: string[];
}

export function buildDebugOverlaySections(
  debugViewModel: GameplayDebugSnapshot,
  viewport: { width: number; height: number }
): DebugOverlaySections {
  const compact = viewport.width <= 420 || viewport.height <= 640;
  const gameplay = [
    `PHS ${debugViewModel.phase}`,
    `DOOM ${debugViewModel.timers.doomClockSec.toFixed(1)}${debugViewModel.timers.doomFrozen ? " (F)" : ""}`,
    `ROT ${debugViewModel.timers.timeToNextRotationSec.toFixed(2)}S`,
    `ANM R:${debugViewModel.timers.rotationAnimRemainingSec.toFixed(2)} C:${debugViewModel.timers.collapseAnimRemainingSec.toFixed(2)} S:${debugViewModel.timers.swapAnimRemainingSec.toFixed(2)}`,
    `SCR R:${debugViewModel.scoreFlow.runTotalScore} D:${debugViewModel.scoreFlow.displayScore} DELTA:${debugViewModel.scoreFlow.displayDelta}`,
    `QUE SW:${debugViewModel.actionQueues.queuedSwaps} SC:${debugViewModel.actionQueues.pendingScoreBatches} DF:${debugViewModel.actionQueues.deferredCollapseSteps}`,
    `TRN ROT:${debugViewModel.transitions.pendingRotation ?? "-"} WARN:${debugViewModel.transitions.warningActive ? "Y" : "N"} CH:${debugViewModel.scoreFlow.chainDepth}`
  ];
  const bot: string[] = [];
  if (debugViewModel.botDiagnostics != null) {
    const diag = debugViewModel.botDiagnostics;
    bot.push(
      `BOT ${diag.selectedBucket.toUpperCase()} LEAD:${diag.leadScoreDiff}`,
      `WGT S:${diag.effectiveWeights.strong.toFixed(2)} N:${diag.effectiveWeights.neutral.toFixed(2)} M:${diag.effectiveWeights.mistake.toFixed(2)}`,
      `SNW T:${diag.antiSnowballTarget.toFixed(2)} E:${diag.antiSnowballEase.toFixed(2)} ST:${diag.leadStartScoreDiff}`,
      `BKT S:${diag.bucketsAvailable.strong} N:${diag.bucketsAvailable.neutral} M:${diag.bucketsAvailable.mistake}`
    );
  }

  if (!compact) {
    return { compact, gameplay, bot };
  }

  return {
    compact,
    gameplay: [
      gameplay[0] ?? "",
      gameplay[1] ?? "",
      gameplay[4] ?? "",
      gameplay[5] ?? ""
    ],
    bot: bot.slice(0, 2)
  };
}

export function applyPhaseScopedShopUiRects(phase: GamePhase, rects: UiRects): UiRects {
  if (phase !== "SHOP" && phase !== "GEM_SHOP") {
    return rects;
  }

  const phaseScopedRects: UiRects = { ...rects };
  if (phase === "SHOP") {
    for (const key of GEM_SHOP_BUY_KEYS) {
      delete phaseScopedRects[key];
    }
    for (const key of IAP_BUY_KEYS) {
      delete phaseScopedRects[key as keyof typeof phaseScopedRects];
    }
    delete phaseScopedRects.shopRestoreIap;
    delete phaseScopedRects.shopIapConfirm;
    delete phaseScopedRects.shopIapCancel;
    delete phaseScopedRects.shopIapDismiss;
    delete phaseScopedRects.gemShopBack;
    return phaseScopedRects;
  }

  delete phaseScopedRects.gemShop;
  return phaseScopedRects;
}

export function isHeaderGemHotspotEnabledForPhase(phase: GamePhase): boolean {
  return phase === "TITLE" || phase === "SETTINGS" || phase === "PROFILE_SETTINGS";
}

export const POWERUP_ICON_PATHS: Record<PowerupKind, string> = {
  HINT: "/assets/raw/powerup_hint.png",
  ROWRIP: "/assets/raw/powerup_rowrip.png",
  COLCUT: "/assets/raw/powerup_colcut.png",
  TRANSMUTE: "/assets/raw/powerup_transmute.png",
  ZIGZAGBOLT: "/assets/raw/powerup_zigzagbolt.png"
};

export const SOLO_SELECTOR_LOGO_PATHS: Record<SoloMode, string> = {
  classic: "/assets/raw/classic-logo.png",
  "row-rush": "/assets/raw/row-rush-logo.png",
  clearout: "/assets/raw/clearout-logo.png",
  "color-hunt": "/assets/raw/color-hunt-logo.png",
  turbomaxxing: "/assets/raw/turbomaxxing-logo.png"
};

export const SOLO_MODE_BACKGROUND_PATHS: Record<SoloMode, string> = {
  classic: "/assets/raw/classic-bg.webp",
  "row-rush": "/assets/raw/row-rush-bg.webp",
  clearout: "/assets/raw/clear-out-bg.webp",
  "color-hunt": "/assets/raw/color-hunt.webp",
  turbomaxxing: "/assets/raw/bg-turbomaxxing.webp"
};

export function soloSelectDescription(mode: SoloMode): string {
  if (mode === "row-rush") return "20 rounds, 10 seconds each. Score only inside highlighted rows, lock one end-of-round turn, and chase the perfect-round bonus.";
  if (mode === "clearout") return "Empty the entire board before the doom clock runs out. Every tile counts.";
  if (mode === "color-hunt") return "Track two target colors through level 10, then hunt one target color per stage with an endless level-20 loop.";
  if (mode === "turbomaxxing") return "Smaller 7x7 board, double-speed rotations, and intense doom pressure. Pure chaos.";
  return "Standard rules. Match tiles, clear the board, and survive as long as you can.";
}

export function soloModeLockCaption(lockState: FeatureLockRenderState): string {
  if (lockState.signInRequired) {
    return `SIGN IN AND REACH LV ${lockState.requiredLevel}`;
  }
  return `UNLOCKS AT LV ${lockState.requiredLevel}`;
}

interface CollapseMotionStyle {
  progress: number;
  yLiftPx: number;
  scaleX: number;
  scaleY: number;
}

export function collapseStyleForTierAndFeedbackMode(
  tier: MatchTier,
  raw: number,
  mode: VersusFeedbackMode
): CollapseMotionStyle {
  if (mode === "off") {
    return {
      progress: easeOutQuad(raw),
      yLiftPx: 0,
      scaleX: 1,
      scaleY: 1
    };
  }

  if (tier === 3) {
    return {
      progress: easeOutQuad(raw),
      yLiftPx: 0,
      scaleX: 1,
      scaleY: 1
    };
  }

  if (tier === 4) {
    const progress = easeOutCubic(raw);
    const liftScale = mode === "reduced" ? 0.45 : 1;
    return {
      progress,
      yLiftPx: -Math.sin(Math.PI * progress) * TILE_PIXEL_SIZE * 0.06 * liftScale,
      scaleX: 1,
      scaleY: 1
    };
  }

  const progress = easeOutBack(raw);
  let scaleX = 1;
  let scaleY = 1;
  if (raw > 0.72) {
    const settleT = clamp01((raw - 0.72) / 0.28);
    const pulseScale = mode === "reduced" ? 0.4 : 1;
    const pulse = Math.sin(Math.PI * settleT) * 0.18 * pulseScale;
    scaleX = 1 + pulse;
    scaleY = 1 - pulse * 0.6;
  }

  const tierFiveLiftScale = mode === "reduced" ? 0.5 : 1;

  return {
    progress,
    yLiftPx: -Math.sin(Math.PI * progress) * TILE_PIXEL_SIZE * 0.03 * tierFiveLiftScale,
    scaleX,
    scaleY
  };
}

export function resolveCollapseClearFxSpriteId(
  specialFx: CollapseAnimState["steps"][number]["specialFx"],
  tier: MatchTier
): SpriteId {
  if (specialFx === "STEEL_CRUSH") {
    return "fx_pop";
  }
  if (specialFx === "WIPE_DISSOLVE") {
    return "fx_rotateflash";
  }
  if (specialFx === "POWERUP_TRANSMUTE" || specialFx === "POWERUP_HINT") {
    return "fx_wipewave";
  }
  if (specialFx === "POWERUP_ZIGZAG") {
    return "fx_pop";
  }
  if (
    specialFx === "BOMB_EXPLOSION"
    || specialFx === "POWERUP_ROW_SWEEP"
    || specialFx === "POWERUP_COL_SWEEP"
    || tier >= 4
  ) {
    return "fx_explosion";
  }
  return "fx_pop";
}

export function hypeScaleForFeedbackMode(mode: VersusFeedbackMode, timerSec: number): number {
  const baseScale = 1 + (1 - Math.min(1, timerSec * 3)) * 0.3;
  if (mode === "reduced") {
    return 1 + (baseScale - 1) * 0.45;
  }
  if (mode === "off") {
    return 1;
  }
  return baseScale;
}

export function tileColorSpriteId(color: number): SpriteId {
  const ids: SpriteId[] = [
    "tile_00",
    "tile_01",
    "tile_02",
    "tile_03",
    "tile_04",
    "tile_05",
    "tile_06",
    "tile_07",
    "tile_08",
    "tile_09",
    "tile_10",
    "tile_11"
  ];
  return ids[Math.min(ids.length - 1, Math.max(0, color))];
}

export function specialSpriteId(special: SpecialKind): SpriteId {
  if (special === "BOMB_3") return "bomb_3";
  if (special === "BOMB_4") return "bomb_4";
  if (special === "BOMB_5") return "bomb_5";
  return "wipe";
}

export function specialSpawnLabel(kind: SpecialKind): { label: string; color: string } {
  switch (kind) {
    case "BOMB_3": return { label: "BOMB!", color: "#ff9933" };
    case "BOMB_4": return { label: "BIG BOMB!", color: "#ff6600" };
    case "BOMB_5": return { label: "NUKE!", color: "#ff3333" };
    case "WIPE":   return { label: "WIPE!", color: "#cc44ff" };
    default:       return { label: "SPECIAL!", color: "#00ccff" };
  }
}

// ── Layout helpers (Phase 2 refactor) ─────────────────────────────────────

export interface GameOverLayout {
  logoY: number;
  logoHeight: number;
  logoBottomY: number;
  totalScoreY: number;
  totalScoreValueY: number;
  columnsY: number;
  columnLeftCx: number;
  columnRightCx: number;
  newHighScoreY: number;
  coinsY: number;
  xpY: number;
  leaderboardTitleY: number;
  leaderboardCollapsed: Rect;
  leaderboardExpanded: Rect;
  leaderboard: Rect;
  start: Rect;
  mainMenu: Rect;
  leaderboardToggle: Rect;
}

export interface GameOverLayoutOptions {
  canvasHeight?: number;
  logoY?: number;
  logoHeight?: number;
  hasNewHighScore?: boolean;
}

export function resolveGameOverLayout(centerX: number, options: GameOverLayoutOptions = {}): GameOverLayout {
  const menuCols = menuButtonColumns(centerX);
  const canvasHeight = options.canvasHeight ?? CANVAS_HEIGHT;
  const logoY = options.logoY ?? 50;
  const logoHeight = options.logoHeight ?? 0;
  const hasNewHighScore = options.hasNewHighScore ?? false;
  const logoBottomY = logoY + logoHeight;
  const scoreY = Math.max(155, logoBottomY + 10);
  const scoreValueY = scoreY + 14;
  const highScoreY = scoreY + 38;
  const highScoreBlockH = hasNewHighScore ? 22 : 0;
  const coinsY = scoreY + 54 + highScoreBlockH;
  const xpY = coinsY + 18;
  const leaderboardGap = 24;
  const buttonGap = 18;
  const columnsY = coinsY;
  const colSpacing = menuCols.fullW / 4;
  const lbY = xpY + leaderboardGap;
  const lbTitleY = lbY - 10;
  const maxButtonY = canvasHeight - (isMobileViewport() ? 24 : 22) - menuBtnH();
  const availableLbH = Math.max(0, maxButtonY - buttonGap - lbY);
  const leaderboardH = Math.min(200, availableLbH);
  const leaderboardRect = { x: menuCols.fullX, y: lbY, w: menuCols.fullW, h: leaderboardH };
  const buttonY = leaderboardRect.y + leaderboardRect.h + buttonGap;
  return {
    logoY,
    logoHeight,
    logoBottomY,
    totalScoreY: scoreY,
    totalScoreValueY: scoreValueY,
    columnsY,
    columnLeftCx: centerX - colSpacing,
    columnRightCx: centerX + colSpacing,
    newHighScoreY: highScoreY,
    coinsY,
    xpY,
    leaderboardTitleY: lbTitleY,
    leaderboardCollapsed: leaderboardRect,
    leaderboardExpanded: leaderboardRect,
    leaderboard: leaderboardRect,
    start: { x: menuCols.leftX, y: buttonY, w: menuCols.halfW, h: menuBtnH() },
    mainMenu: { x: menuCols.rightX, y: buttonY, w: menuCols.halfW, h: menuBtnH() },
    leaderboardToggle: { x: menuCols.fullX, y: lbY + Math.max(leaderboardH - 20, 0), w: menuCols.fullW, h: 20 }
  };
}

export interface VersusResultLayout {
  titleY: number;
  reasonY: number;
  tableY: number;
  rewardsGemsY: number;
  rewardsXpY: number;
  xpBarY: number;
  mainMenu: Rect;
}

export function resolveVersusResultLayout(centerX: number): VersusResultLayout {
  const cols = menuButtonColumns(centerX);
  return {
    titleY: 96,
    reasonY: 126,
    tableY: 168,
    rewardsGemsY: 316,
    rewardsXpY: 334,
    xpBarY: 348,
    mainMenu: { x: cols.fullX, y: 476, w: cols.fullW, h: menuBtnH() }
  };
}

export interface SoloLeaderboardSurfaceState {
  entries: LeaderboardEntry[];
  loading: boolean;
}

export function resolveSoloLeaderboardSurfaceState(
  entries: LeaderboardEntry[],
  leaderboardLoading: boolean,
  leaderboardMode: SoloMode | null,
  leaderboardDisplayMode: SoloMode
): SoloLeaderboardSurfaceState {
  const matchingMode = leaderboardMode === leaderboardDisplayMode;
  return {
    entries: matchingMode ? entries : [],
    loading: leaderboardLoading
  };
}

export interface VersusSuddenDeathBannerState {
  visible: boolean;
  headline: string;
  countdownText: string;
  targetText: string;
}

export interface VersusSuddenDeathBannerLayout {
  panel: Rect;
  headlineY: number;
  countdownY: number;
  targetY: number;
}

export function resolveVersusSuddenDeathBannerState(
  suddenDeath: VersusMatchRenderData["suddenDeath"]
): VersusSuddenDeathBannerState {
  if (suddenDeath == null) {
    return {
      visible: false,
      headline: "",
      countdownText: "",
      targetText: ""
    };
  }
  return {
    visible: true,
    headline: "SUDDEN DEATH",
    countdownText: `${Math.ceil(Math.max(0, suddenDeath.remainingSec))}`,
    targetText: `TARGET ${Math.max(0, Math.floor(suddenDeath.targetScore)).toLocaleString()}`
  };
}

export function resolveVersusSuddenDeathBannerLayout(
  isMobile: boolean,
  canvasWidth: number
): VersusSuddenDeathBannerLayout {
  const panelW = isMobile ? Math.max(188, canvasWidth - 28) : 220;
  const panelH = isMobile ? 58 : 60;
  const panelX = Math.floor((canvasWidth - panelW) / 2);
  const panelY = isMobile ? 90 : 8;
  return {
    panel: { x: panelX, y: panelY, w: panelW, h: panelH },
    headlineY: panelY + 14,
    countdownY: panelY + 33,
    targetY: panelY + 49
  };
}

export interface SettingsLayout {
  rows: Rect[];
  prev: Rect;
  next: Rect;
  back: Rect;
}

export function resolveSettingsLayout(
  centerX: number,
  page: number,
  canUseDebugSettings = false
): SettingsLayout {
  const rowW = menuFullButtonW();
  const rowX = centerX - rowW / 2;
  const firstRowY = 170;
  const defaultGap = menuGap();
  const page0Gap = isMobileViewport() ? 18 : 14;
  const settingsPages = getSettingsPages({ surface: "main", canUseDebugSettings });
  const activePage = getSettingsPageDefinition({ surface: "main", canUseDebugSettings }, page);
  const heights = activePage.mainRowHeights;
  const gap = activePage.mainGapVariant === "sound" ? page0Gap : defaultGap;
  const rows: Rect[] = [];
  let currentY = firstRowY;
  for (const h of heights) {
    rows.push({ x: rowX, y: currentY, w: rowW, h });
    currentY += h + gap;
  }

  // Footer anchored to densest page's content bottom
  let maxContentBottom = 0;
  for (const settingsPage of settingsPages) {
    const hs = settingsPage.mainRowHeights;
    const g = settingsPage.mainGapVariant === "sound" ? page0Gap : defaultGap;
    let y = firstRowY;
    for (const rh of hs) { y += rh + g; }
    maxContentBottom = Math.max(maxContentBottom, y - g);
  }
  const rowDrivenBtnY = maxContentBottom + 12;
  const cols = menuButtonColumns(centerX);
  const btnH = menuBtnH();
  const settingsFooterBottomMargin = 20;
  const bottomAnchoredBtnY = CANVAS_HEIGHT - MENU_CONTENT_OFFSET_PX - settingsFooterBottomMargin - btnH;
  const maxBtnY = CANVAS_HEIGHT - MENU_CONTENT_OFFSET_PX - btnH;
  const btnY = Math.min(Math.max(rowDrivenBtnY, bottomAnchoredBtnY), maxBtnY);

  // Three-button footer: [â—„ PREV] [BACK] [NEXT â–º]
  const btnGap = menuTwoColGap();
  const navW = Math.floor((cols.fullW - btnGap * 2) * 0.25);
  const backW = cols.fullW - navW * 2 - btnGap * 2;
  const prevX = cols.fullX;
  const backX = prevX + navW + btnGap;
  const nextX = backX + backW + btnGap;

  return {
    rows,
    prev: { x: prevX, y: btnY, w: navW, h: btnH },
    back: { x: backX, y: btnY, w: backW, h: btnH },
    next: { x: nextX, y: btnY, w: navW, h: btnH }
  };
}

export interface TitleMenuLayout {
  start: Rect;
  tutorial: Rect;
  shop: Rect;
  leaderboard: Rect;
  settings: Rect;
  versusMode?: Rect;
  musicY: number;
}

export function resolveTitleMenuLayout(centerX: number, versusEnabled: boolean): TitleMenuLayout {
  return {
    start: { x: centerX - 70, y: 228, w: 140, h: 44 },
    tutorial: { x: centerX - 124, y: 282, w: 110, h: 30 },
    shop: { x: centerX + 14, y: 282, w: 110, h: 30 },
    leaderboard: { x: centerX - 124, y: 320, w: 110, h: 30 },
    settings: { x: centerX + 14, y: 320, w: 110, h: 30 },
    versusMode: versusEnabled ? { x: centerX - 60, y: 358, w: 120, h: 30 } : undefined,
    musicY: versusEnabled ? 398 : 362
  };
}

export interface MenuHeaderTopRightActionRects {
  friends: Rect;
  settings: Rect;
}

const MOBILE_MENU_SAFE_TOP_FALLBACK_PX = 12;

export function resolveMenuSafeTopOffset(
  topInsetPx: number,
  mobile = isMobileViewport()
): number {
  if (!mobile) {
    return 0;
  }
  return Math.round(Math.max(0, topInsetPx, MOBILE_MENU_SAFE_TOP_FALLBACK_PX));
}

export function resolveMenuHeaderTopRightActionRects(
  canvasWidth: number,
  mobile = isMobileViewport(),
  topSafeOffsetPx = 0
): MenuHeaderTopRightActionRects {
  const topBarY = 9 + Math.max(0, topSafeOffsetPx);
  const iconBtnSize = mobile ? 28 : 18;
  const pfpSize = mobile ? 26 : 20;
  const row1CenterY = topBarY + pfpSize / 2;
  const gearX = canvasWidth - 8 - iconBtnSize;
  const friendsIconX = gearX - iconBtnSize - 8;

  return {
    settings: { x: gearX - 2, y: row1CenterY - iconBtnSize / 2 - 2, w: iconBtnSize + 4, h: iconBtnSize + 4 },
    friends: { x: friendsIconX - 2, y: row1CenterY - iconBtnSize / 2 - 2, w: iconBtnSize + 4, h: iconBtnSize + 4 }
  };
}

export function resolveProfileCloseButtonScreenRect(
  canvasWidth: number,
  mobile = isMobileViewport(),
  topSafeOffsetPx = 0
): Rect {
  const headerRects = resolveMenuHeaderTopRightActionRects(canvasWidth, mobile, topSafeOffsetPx);
  const closeSize = 24;
  return {
    x: canvasWidth - closeSize - 10,
    y: headerRects.settings.y + headerRects.settings.h + 6,
    w: closeSize,
    h: closeSize
  };
}

export function resolveProfileCloseButtonRect(
  canvasWidth: number,
  mobile = isMobileViewport(),
  topSafeOffsetPx = 0
): Rect {
  const closeRect = resolveProfileCloseButtonScreenRect(canvasWidth, mobile, topSafeOffsetPx);
  return {
    ...closeRect,
    y: Math.max(0, closeRect.y - MENU_CONTENT_OFFSET_PX)
  };
}

export function resolveWorldMapCloseButtonRect(canvasWidth: number): Rect {
  const closeSize = 32;
  return {
    x: canvasWidth - closeSize - UI_EDGE_PAD,
    y: 12,
    w: closeSize,
    h: closeSize
  };
}

const MOBILE_PROFILE_FILTER_PILL_H = 22;
const MOBILE_PROFILE_FILTER_PILL_GAP = 6;
const MOBILE_PROFILE_FILTER_PILL_TRAILING_INSET = 16;
const MOBILE_PROFILE_FILTER_PILL_X_PAD = 10;
const MOBILE_PROFILE_FILTER_ALL_MIN_W = 36;
const MOBILE_PROFILE_FILTER_WALLET_MIN_W = 64;

export function resolveMobileProfileFilterTabRects(
  canvasWidth: number,
  pillY: number,
  allLabelWidth: number,
  walletLabelWidth: number
): { allRect: Rect; walletRect: Rect } {
  const allW = Math.max(
    MOBILE_PROFILE_FILTER_ALL_MIN_W,
    Math.ceil(allLabelWidth) + MOBILE_PROFILE_FILTER_PILL_X_PAD * 2
  );
  const walletW = Math.max(
    MOBILE_PROFILE_FILTER_WALLET_MIN_W,
    Math.ceil(walletLabelWidth) + MOBILE_PROFILE_FILTER_PILL_X_PAD * 2
  );
  const walletRect: Rect = {
    x: canvasWidth - MOBILE_PROFILE_FILTER_PILL_TRAILING_INSET - walletW,
    y: pillY,
    w: walletW,
    h: MOBILE_PROFILE_FILTER_PILL_H
  };
  const allRect: Rect = {
    x: walletRect.x - MOBILE_PROFILE_FILTER_PILL_GAP - allW,
    y: pillY,
    w: allW,
    h: MOBILE_PROFILE_FILTER_PILL_H
  };
  return { allRect, walletRect };
}

export function resolveTitleWorldMapLabelAnchor(
  buildingKey: string,
  rect: Rect,
  canvasCenterX = CANVAS_WIDTH / 2
): { x: number; y: number } {
  return {
    x: buildingKey === "battle" ? canvasCenterX : rect.x + rect.w / 2,
    y: rect.y + rect.h / 2
  };
}

export function resolveTitleLogoScale(timeSec: number): number {
  return 1.05 - 0.05 * Math.cos((timeSec / 4) * Math.PI * 2);
}

export function resolvePortraitContainRect(
  srcWidth: number,
  srcHeight: number,
  dest: Rect,
  verticalAlign: "center" | "bottom" = "center"
): Rect {
  const safeSrcWidth = Math.max(1, srcWidth);
  const safeSrcHeight = Math.max(1, srcHeight);
  const scale = Math.min(dest.w / safeSrcWidth, dest.h / safeSrcHeight);
  const drawW = safeSrcWidth * scale;
  const drawH = safeSrcHeight * scale;
  const drawX = dest.x + (dest.w - drawW) / 2;
  const drawY = verticalAlign === "bottom"
    ? dest.y + dest.h - drawH
    : dest.y + (dest.h - drawH) / 2;
  return { x: drawX, y: drawY, w: drawW, h: drawH };
}

export function resolveProfileBrowsePortraitMetrics(
  canvasHeight: number,
  buttonsBottom: number,
  bobY: number
): { topY: number; bottomY: number; width: number; height: number } {
  const topY = buttonsBottom - 32 + bobY;
  const bottomY = canvasHeight - MENU_CONTENT_OFFSET_PX;
  const height = Math.max(1, bottomY - topY);
  const width = height * 0.85;
  return { topY, bottomY, width, height };
}

export interface DrawBoardResult {
  board: Rect;
  boardGridSize: number;
  rotateLeft: Rect;
  rotateRight: Rect;
  keyTrigger: Rect;
  grooveMeter: Rect;
  grooveMixer: Rect;
  characterHub?: Rect;
}

export interface DrawBoardOptions {
  hideCornerCharacter?: boolean;
}

export interface TitleGemBurst {
  startMs: number;
  durationMs: number;
  count: number;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  kind?: "gem" | "coin";
}

export function truncName(name: string, maxLen: number): string {
  return name.length > maxLen ? name.slice(0, maxLen) : name;
}

export function offsetUiRects(rects: UiRects, dy: number): UiRects {
  const out: UiRects = {};
  for (const [key, val] of Object.entries(rects)) {
    if (val == null) {
      continue;
    }
    if (typeof val === "number") {
      (out as Record<string, unknown>)[key] = val;
      continue;
    }
    if (Array.isArray(val)) {
      (out as Record<string, unknown>)[key] = (val as Rect[]).map((r) => ({ x: r.x, y: r.y + dy, w: r.w, h: r.h }));
    } else {
      (out as Record<string, unknown>)[key] = { x: (val as Rect).x, y: (val as Rect).y + dy, w: (val as Rect).w, h: (val as Rect).h };
    }
  }
  return out;
}

export function menuButtonColumns(centerX: number): {
  fullX: number;
  fullW: number;
  leftX: number;
  rightX: number;
  halfW: number;
  gap: number;
} {
  const fullW = menuFullButtonW();
  const halfW = menuHalfButtonW();
  const gap = menuTwoColGap();
  const fullX = centerX - fullW / 2;
  const leftX = fullX;
  const rightX = leftX + halfW + gap;
  return { fullX, fullW, leftX, rightX, halfW, gap };
}

export function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function formatHms(totalSec: number): string {
  const clamped = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(clamped / 3600);
  const m = Math.floor((clamped % 3600) / 60);
  const s = clamped % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

export function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  const radius = Math.max(0, Math.min(r, Math.min(w, h) / 2));
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

export function drawImageContain(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  rect: Rect
): void {
  const naturalW = image.naturalWidth || image.width;
  const naturalH = image.naturalHeight || image.height;
  if (naturalW <= 0 || naturalH <= 0 || rect.w <= 0 || rect.h <= 0) {
    return;
  }
  const scale = Math.min(rect.w / naturalW, rect.h / naturalH);
  const drawW = Math.max(1, Math.round(naturalW * scale));
  const drawH = Math.max(1, Math.round(naturalH * scale));
  const drawX = Math.round(rect.x + (rect.w - drawW) / 2);
  const drawY = Math.round(rect.y + (rect.h - drawH) / 2);
  ctx.drawImage(image, drawX, drawY, drawW, drawH);
}

/** Draw a settings icon button at the given rect using the flower SVG or fallback. */
export function drawGearButton(
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  size: number,
  iconImage?: HTMLImageElement | null
): void {
  roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, 6);
  ctx.fillStyle = "rgba(24,36,64,0.92)";
  ctx.fill();
  roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, 6);
  ctx.strokeStyle = "#7ca8ea";
  ctx.lineWidth = 1.2;
  ctx.stroke();
  ctx.lineWidth = 1;

  const pad = Math.round(size * 0.22);
  const iconSize = size - pad * 2;
  if (iconImage != null) {
    ctx.drawImage(iconImage, rect.x + pad, rect.y + pad, iconSize, iconSize);
  } else {
    // Fallback: simple gear outline
    const cx = rect.x + size / 2;
    const cy = rect.y + size / 2;
    const outerR = size * 0.34;
    const innerR = size * 0.18;
    const teeth = 6;
    const toothDepth = size * 0.1;
    ctx.save();
    ctx.fillStyle = "#e9f4ff";
    ctx.beginPath();
    for (let i = 0; i < teeth; i++) {
      const a1 = (i / teeth) * Math.PI * 2 - Math.PI / 2;
      const a2 = ((i + 0.35) / teeth) * Math.PI * 2 - Math.PI / 2;
      const a3 = ((i + 0.65) / teeth) * Math.PI * 2 - Math.PI / 2;
      const a4 = ((i + 1) / teeth) * Math.PI * 2 - Math.PI / 2;
      const or = outerR + toothDepth;
      if (i === 0) ctx.moveTo(cx + Math.cos(a1) * or, cy + Math.sin(a1) * or);
      else ctx.lineTo(cx + Math.cos(a1) * or, cy + Math.sin(a1) * or);
      ctx.lineTo(cx + Math.cos(a2) * or, cy + Math.sin(a2) * or);
      ctx.lineTo(cx + Math.cos(a3) * outerR, cy + Math.sin(a3) * outerR);
      ctx.lineTo(cx + Math.cos(a4) * outerR, cy + Math.sin(a4) * outerR);
    }
    ctx.closePath();
    ctx.fill();
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = "source-over";
    ctx.strokeStyle = "#e9f4ff";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }
}

export function resolveDailyLoginTierStroke(tier: DailyLoginRewardTier): string {
  switch (tier) {
    case "bigger":
      return "#f3c567";
    case "substantial":
      return "#ff6f9f";
    default:
      return "#74b7ff";
  }
}

export function formatDailyLoginDayLabel(day: number, mobile: boolean): string {
  return mobile ? String(day) : `D${day}`;
}

export interface DailyLoginHelperLinesParams {
  mobile: boolean;
  isGuestUser: boolean;
  claimPending: boolean;
  loading: boolean;
  status: DailyLoginBonusStatus | null;
  rewardCoins: number;
  rewardGems: number;
}

export function resolveDailyLoginHelperLines(params: DailyLoginHelperLinesParams): string[] {
  const { mobile, isGuestUser, claimPending, loading, status, rewardCoins, rewardGems } = params;
  if (!mobile) {
    if (isGuestUser) {
      return ["PREVIEW THE CALENDAR. SIGN IN TO START YOUR UTC STREAK."];
    }
    if (claimPending) {
      return ["STAMPING TODAY'S REWARD..."];
    }
    if (status?.canClaimToday) {
      return [`TAP DAY ${status.claimableDay ?? 1} TO CLAIM +${rewardCoins} COINS +${rewardGems} GEMS`];
    }
    if (loading && status == null) {
      return ["SYNCING YOUR STREAK..."];
    }
    return ["CHECK BACK AT 00:00 UTC"];
  }

  if (isGuestUser) {
    return ["PREVIEW THE CALENDAR", "SIGN IN TO START YOUR UTC STREAK."];
  }
  if (claimPending) {
    return ["STAMPING TODAY'S REWARD..."];
  }
  if (status?.canClaimToday) {
    return [`TAP DAY ${status.claimableDay ?? 1} TO CLAIM`, `+${rewardCoins} COINS +${rewardGems} GEMS`];
  }
  if (loading && status == null) {
    return ["SYNCING YOUR STREAK..."];
  }
  return ["CHECK BACK AT 00:00 UTC"];
}

export interface DailyLoginTextFitParams {
  text: string;
  maxWidth: number;
  basePx: number;
  minPx: number;
  measureWidthAtPx: (text: string, fontPx: number) => number;
}

export function resolveDailyLoginTextFitPx(params: DailyLoginTextFitParams): number {
  const { text, maxWidth, basePx, minPx, measureWidthAtPx } = params;
  if (text.length === 0 || maxWidth <= 0 || basePx <= minPx) {
    return basePx;
  }

  const steps = Math.max(0, Math.round((basePx - minPx) * 2));
  for (let step = 0; step <= steps; step += 1) {
    const fontPx = basePx - step * 0.5;
    if (measureWidthAtPx(text, fontPx) <= maxWidth) {
      return fontPx;
    }
  }
  return minPx;
}

export interface DailyLoginModalTypography {
  innerWidth: number;
  titleFontPx: number;
  titleWidth: number;
  subtitleFontPx: number;
  subtitleWidth: number;
  badgeFontPx: number;
  badgeTextWidth: number;
  legendFontPx: number;
  legendEntryWidths: number[];
  legendTotalWidth: number;
  helperFontPx: number;
  helperLineWidths: number[];
}

export interface DailyLoginModalTypographyParams {
  mobile: boolean;
  panelWidth: number;
  badgeText: string;
  helperLines: readonly string[];
  measureWidthAtPx: (text: string, fontPx: number) => number;
}

function measureDailyLoginLegendWidths(
  labels: readonly string[],
  fontPx: number,
  measureWidthAtPx: (text: string, fontPx: number) => number
): { entryWidths: number[]; totalWidth: number } {
  const entryWidths = labels.map((label) => (
    DAILY_LOGIN_MODAL_LEGEND_SWATCH_SIZE
    + DAILY_LOGIN_MODAL_LEGEND_LABEL_GAP
    + measureWidthAtPx(label, fontPx)
  ));
  const totalWidth = entryWidths.reduce((sum, width) => sum + width, 0)
    + Math.max(0, labels.length - 1) * DAILY_LOGIN_MODAL_LEGEND_ENTRY_GAP;
  return { entryWidths, totalWidth };
}

export function resolveDailyLoginModalTypography(
  params: DailyLoginModalTypographyParams
): DailyLoginModalTypography {
  const { mobile, panelWidth, badgeText, helperLines, measureWidthAtPx } = params;
  const innerWidth = panelWidth - (mobile ? 40 : 28);

  const titleFontPx = mobile
    ? resolveDailyLoginTextFitPx({
      text: DAILY_LOGIN_MODAL_TITLE,
      maxWidth: innerWidth,
      basePx: 7.5,
      minPx: 6,
      measureWidthAtPx
    })
    : 9;
  const subtitleFontPx = mobile
    ? resolveDailyLoginTextFitPx({
      text: DAILY_LOGIN_MODAL_SUBTITLE,
      maxWidth: innerWidth,
      basePx: 4.75,
      minPx: 4,
      measureWidthAtPx
    })
    : 4.5;
  const badgeFontPx = mobile
    ? resolveDailyLoginTextFitPx({
      text: badgeText,
      maxWidth: innerWidth - 20,
      basePx: 4.5,
      minPx: 3.5,
      measureWidthAtPx
    })
    : 4.5;

  let legendFontPx = mobile ? 4.25 : 4.5;
  let legendMetrics = measureDailyLoginLegendWidths(DAILY_LOGIN_MODAL_LEGEND_LABELS, legendFontPx, measureWidthAtPx);
  while (mobile && legendFontPx > 3.5 && legendMetrics.totalWidth > innerWidth) {
    legendFontPx -= 0.5;
    legendMetrics = measureDailyLoginLegendWidths(DAILY_LOGIN_MODAL_LEGEND_LABELS, legendFontPx, measureWidthAtPx);
  }

  const helperBasePx = mobile ? (helperLines.length > 1 ? 4.1 : 4.4) : 4.5;
  const helperMinPx = mobile ? 3.5 : 4.5;
  const helperFontPx = helperLines.length === 0
    ? helperBasePx
    : helperLines.reduce((minFontPx, line) => Math.min(
      minFontPx,
      resolveDailyLoginTextFitPx({
        text: line,
        maxWidth: innerWidth,
        basePx: helperBasePx,
        minPx: helperMinPx,
        measureWidthAtPx
      })
    ), helperBasePx);

  return {
    innerWidth,
    titleFontPx,
    titleWidth: measureWidthAtPx(DAILY_LOGIN_MODAL_TITLE, titleFontPx),
    subtitleFontPx,
    subtitleWidth: measureWidthAtPx(DAILY_LOGIN_MODAL_SUBTITLE, subtitleFontPx),
    badgeFontPx,
    badgeTextWidth: measureWidthAtPx(badgeText, badgeFontPx),
    legendFontPx,
    legendEntryWidths: legendMetrics.entryWidths,
    legendTotalWidth: legendMetrics.totalWidth,
    helperFontPx,
    helperLineWidths: helperLines.map((line) => measureWidthAtPx(line, helperFontPx))
  };
}

export function dailyLoginTierFill(tier: DailyLoginRewardTier, claimed: boolean): string {
  if (claimed) {
    return "rgba(28,52,68,0.94)";
  }
  switch (tier) {
    case "bigger":
      return "rgba(60,44,18,0.94)";
    case "substantial":
      return "rgba(62,24,44,0.96)";
    default:
      return "rgba(18,28,52,0.94)";
  }
}

